import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { DataTable, columnTypes, percent, ratio, styles, pitchTypeDisplayName, number } from 'yankees-react-utils';
import { pitchFreq, kzoneAccuracy } from './utils';
import ReportCard from '../../../../components/ReportCard';
import ReportNote from '../../../../components/ReportNote';

const StyledReportCard = styled(ReportCard)`
    .table {
        overflow: hidden;
        text-align: center;
    }
    th, td {
        border-right: 1px solid ${styles.theme.border.bombay} !important;
        border-bottom: 1px solid ${styles.theme.border.bombay} !important;
    }
    .highlight {
        background-color: #ffff77 !important;
    }
    tr:last-child td {
        border-bottom: none !important;
    }
    .movement-note {
        div.spinner-border {
            margin: 0 auto;
        }
        div:first-child:not(.spinner-border) {
            width: 100%;
            text-align: left;
        }
        svg {
            margin-left: 4px;
        }
    }
    .high-usage, .low-usage {
        font-weight: bold !important;
    }
    .high-usage {
        background-color: #AACCFF !important;
    }
    .low-usage {
        background-color: #F2B9B4 !important;
    }
`;

const RepertoireTable = ({ pitcherId, levelId, repertoire, numFbInRepertoire, lgavgkzon }) => {
    const { t: translate } = useTranslation();

    const columns = [
        {
            id: 'type',
            title: translate('sections.opponent.player.type'),
            type: columnTypes.NODE,
            transform: (v) => pitchTypeDisplayName(v, true, true, translate),
            cellClass: (v) => `fw-bold ${v.highlight ? 'highlight' : ''}`,
        },
        {
            id: 'velo',
            title: translate('sections.opponent.player.velo-range'),
            type: columnTypes.NODE,
            transform: (v) => (
                <>
                    <div>{v.minvelo && v.maxvelo && v.avgvelo ? `${v.minvelo}-${v.maxvelo} (${v.avgvelo})` : '--'}</div>
                    {!!v.lasttwoweeksminvelo && !!v.lasttwoweeksmaxvelo && !!v.lasttwoweeksavgvelo && (
                        <div>
                            {translate('last-two-weeks')}: {`${v.lasttwoweeksminvelo}-${v.lasttwoweeksmaxvelo} (${v.lasttwoweeksavgvelo})`}
                        </div>
                    )}
                </>
            ),
        },
        {
            id: 'movement',
            title: translate('sections.opponent.player.movement'),
            cellClass: 'movement-note',
            type: columnTypes.NODE,
            transform: (v) => (
                v.isExtra
                    ? <>--</>
                    : (
                        <div className="d-flex justify-content-end">
                            <ReportNote
                                type={levelId === 8 ? 'oppp' : 'mnhp'}
                                section="100"
                                playerId={pitcherId}
                                fxtype={v.fxtype}
                                fxsubtype={v.fxsubtype}
                                levelId={levelId}
                            />
                        </div>
                    )
            ),
        },
        {
            id: 'pit',
            title: '%',
            type: columnTypes.NODE,
            transform: (v) => (v.pitches !== undefined ? percent(ratio(v.pitches, v.pitches_total), 0) : '--'),
            cellClass: (v) => pitchFreq(v, 'pit', 'pitches_total', 'pitches', 'pitches_total', numFbInRepertoire, lgavgkzon),
        },
        {
            id: '00',
            title: '0-0',
            type: columnTypes.NODE,
            transform: (v) => (v.firstpit !== undefined ? percent(ratio(v.firstpit, v.firstpit_total), 0) : '--'),
            cellClass: (v) => pitchFreq(v, '00', 'pitches_total', 'firstpit', 'firstpit_total', numFbInRepertoire, lgavgkzon),
        },
        {
            id: 'ahead',
            title: translate('sections.opponent.player.abbr-hitter-ahead'),
            type: columnTypes.NODE,
            transform: (v) => (v.ahead !== undefined ? percent(ratio(v.ahead, v.ahead_total), 0) : '--'),
            cellClass: (v) => pitchFreq(v, 'ahead', 'pitches_total', 'ahead', 'ahead_total', numFbInRepertoire, lgavgkzon),
        },
        {
            id: '2k',
            title: translate('sections.opponent.team.abbr-two-strike'),
            type: columnTypes.NODE,
            transform: (v) => (v.twostrikes !== undefined ? percent(ratio(v.twostrikes, v.twostrikes_total), 0) : '--'),
            cellClass: (v) => pitchFreq(v, '2k', 'pitches_total', 'twostrikes', 'twostrikes_total', numFbInRepertoire, lgavgkzon),
        },
        {
            id: '32',
            title: '3-2',
            type: columnTypes.NODE,
            transform: (v) => (v.cnt32 !== undefined ? percent(ratio(v.cnt32, v.cnt32_total), 0) : '--'),
            cellClass: (v) => pitchFreq(v, '32', 'pitches_total', 'cnt32', 'cnt32_total', numFbInRepertoire, lgavgkzon),
        },
        {
            id: 'kzon',
            title: translate('baseball.abbr-strikezone'),
            type: columnTypes.NODE,
            transform: (v) => (v.pstrike !== undefined ? percent(ratio(v.pstrike, v.pitches), 0) : '--'),
            cellClass: (v) => (ratio(v.pitches, v.pitches_total) > 0.1 ? kzoneAccuracy(v.pstrike, v.pitches, 'pit', v.fxtype, lgavgkzon) : ''),
        },
        {
            id: 'risp',
            title: `${translate('baseball.abbr-runners-in-position')}${repertoire.length ? ` (${number(repertoire[0].risp_total, 0)})` : ''}`,
            headerClass: 'highlight',
            type: columnTypes.NODE,
            transform: (v) => (v.risp !== undefined ? percent(ratio(v.risp, v.risp_total), 0) : '--'),
            cellClass: (v) => pitchFreq(v, 'risp', 'pitches_total', 'risp', 'risp_total', numFbInRepertoire, lgavgkzon),
        },
        {
            id: '00risp',
            title: `0-0 ${translate('baseball.abbr-runners-in-position')}${repertoire.length ? ` (${number(repertoire[0].risp00_total, 0)})` : ''}`,
            headerClass: 'highlight',
            type: columnTypes.NODE,
            transform: (v) => (v.risp00 !== undefined ? percent(ratio(v.risp00, v.risp00_total), 0) : '--'),
            cellClass: (v) => pitchFreq(v, 'risp00', 'pitches_total', 'risp00', 'risp00_total', numFbInRepertoire, lgavgkzon),
        },
    ];

    return (
        <StyledReportCard title={translate('pitcher-overview.sections.overview')}>
            <DataTable
                columns={columns}
                rows={repertoire.filter((r) => (numFbInRepertoire > 1 || r.fxtype !== 1))}
            />
        </StyledReportCard>
    );
};

export default RepertoireTable;
